import { useCallback, useEffect, useRef, useState } from 'react'

interface UseRequestResult<T> {
  data: T | null
  error: unknown | null
  isLoading: boolean
  run: (request: (signal: AbortSignal) => Promise<T>) => Promise<T | null>
}

export const useRequest = <T>(): UseRequestResult<T> => {
  const requestIdRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isMountedRef = useRef(true)

  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      abortControllerRef.current?.abort()
    }
  }, [])

  const run = useCallback(
    async (request: (signal: AbortSignal) => Promise<T>) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      abortControllerRef.current?.abort()

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setIsLoading(true)
      setError(null)

      try {
        const result = await request(abortController.signal)

        if (
          isMountedRef.current &&
          requestIdRef.current === requestId &&
          !abortController.signal.aborted
        ) {
          setData(result)
        }

        return result
      } catch (currentError) {
        if (
          isMountedRef.current &&
          requestIdRef.current === requestId &&
          !abortController.signal.aborted
        ) {
          setError(currentError)
        }

        return null
      } finally {
        if (isMountedRef.current && requestIdRef.current === requestId) {
          setIsLoading(false)
        }
      }
    },
    [],
  )

  return { data, error, isLoading, run }
}
